import { lazy, type ComponentProps, type ComponentType, type ReactElement } from "react";

const RELOAD_KEY = "nexus.staleChunkReloadAt";
const RELOAD_WINDOW_MS = 30_000;

/** Errors a browser raises when a code-split file cannot be fetched (deleted by a newer deploy, or a network drop). */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically|error loading dynamically|Unable to preload CSS/i.test(
    message,
  );
}

/**
 * Reloads the page once to pick up the current build. A second failure within the window is left to the
 * error screen, so a genuinely broken deploy cannot cause a reload loop.
 */
export function reloadOnceForNewBuild(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

export interface PreloadableComponent<T extends ComponentType<any>> {
  (props: ComponentProps<T>): ReactElement | null;
  /**
   * Warms the module cache ahead of a navigation that will render this
   * route, so the `Suspense` fallback that would otherwise cover the whole
   * app shell resolves instantly instead of after a cold `import()`
   * round-trip. Safe to call more than once and safe to ignore the result;
   * a caller that also renders the component still goes through the normal
   * `lazy()` path, which reuses this same in-flight/resolved promise.
   */
  preload: () => Promise<{ default: T }>;
}

/**
 * `React.lazy` for routes. A page opened before a deploy still points at file names that no longer exist;
 * retry once for a transient network error, then reload to fetch the new build instead of showing an error.
 *
 * Also exposes `.preload()` so a caller (e.g. a list whose rows link to a
 * heavy route) can start the dynamic `import()` before the user commits to
 * navigating — on hover/focus, or eagerly once the list itself has settled —
 * instead of only starting it when `Suspense` first suspends on render.
 */
export function lazyRoute<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): PreloadableComponent<T> {
  let inFlight: Promise<{ default: T }> | null = null;

  const loadWithRetry = async (): Promise<{ default: T }> => {
    try {
      return await load();
    } catch (first) {
      if (!isChunkLoadError(first)) throw first;
      try {
        return await load();
      } catch (second) {
        if (isChunkLoadError(second) && reloadOnceForNewBuild())
          return new Promise<never>(() => undefined);
        throw second;
      }
    }
  };

  const preload = (): Promise<{ default: T }> => {
    if (!inFlight) {
      inFlight = loadWithRetry().catch((error: unknown) => {
        // Let a real failure be retried by a later call (e.g. the user tries
        // again after a network blip) instead of caching a rejected promise.
        inFlight = null;
        throw error;
      });
    }
    return inFlight;
  };

  const Component = lazy(preload) as unknown as PreloadableComponent<T>;
  Component.preload = preload;
  return Component;
}
